import { motion } from "framer-motion";
import { Check, Flame, Gem, Info, Medal, Star, X } from "lucide-react";
import { Button } from "../ui/button";

const TIERS = [
  {
    name: "Diamond Seat",
    price: "USD 200",
    Icon: Gem,
    urgency: "Limited to 15 Pax Only",
    cardClass:
      "pink-glow relative rounded-3xl border border-fira-pink/60 bg-gradient-to-b from-fira-pink/15 to-fira-panel p-8 transition-transform duration-300 hover:-translate-y-2",
    iconClass: "text-fira-gold",
    priceClass: "text-glow text-fira-rose",
    checkClass: "text-fira-rose",
    perks: [
      "Front Row Seating",
      "Extra Masterclass Session bersama Fira & Team",
      "Exclusive Dinner bersama Fira & Team",
      "VIP Merchandise — The New Era",
      "Fira One",
    ],
  },
  {
    name: "Gold Seat",
    price: "USD 100",
    Icon: Medal,
    cardClass:
      "gold-glow relative rounded-3xl border border-[#D4AF37]/50 bg-gradient-to-b from-[#D4AF37]/10 to-fira-panel p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#D4AF37]/75",
    iconClass: "text-[#D4AF37]",
    priceClass: "text-gold-glow text-[#E9CB74]",
    checkClass: "text-[#D4AF37]",
    perks: [
      "Gold Seating — di belakang Diamond",
      "Gold Merchandise — The New Era",
      "Fira One",
    ],
    crossed: [
      "Extra Masterclass Session bersama Fira & Team",
      "Exclusive Dinner bersama Fira & Team",
    ],
  },
  {
    name: "Silver Seat",
    price: "USD 50",
    Icon: Star,
    cardClass:
      "silver-glow relative rounded-3xl border border-[#C7C9D1]/30 bg-gradient-to-b from-[#C7C9D1]/[0.07] to-fira-panel p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#C7C9D1]/55",
    iconClass: "text-[#C7C9D1]",
    priceClass: "text-[#EDEFF4]",
    checkClass: "text-[#C7C9D1]",
    perks: [
      "Silver Seating — di belakang Gold",
      "Basic Merchandise — The New Era",
      "Fira One",
    ],
    crossed: [
      "Extra Masterclass Session bersama Fira & Team",
      "Exclusive Dinner bersama Fira & Team",
    ],
  },
];

export const Experience = ({ onReserve }) => (
  <section id="experience" data-testid="experience-section" className="py-24 md:py-32">
    <div className="mx-auto max-w-6xl px-5">
      <div className="text-center">
        <span className="eyebrow">
          Choose Your Experience
        </span>
        <h2 className="font-display mt-4 text-4xl font-semibold md:text-5xl">
          Three Exclusive <span className="italic text-fira-rose">Seating Experiences</span>
        </h2>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-3" data-testid="tiers-grid">
        {TIERS.map((tier, i) => (
          <motion.div
            key={tier.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            data-testid={`tier-${tier.name.split(" ")[0].toLowerCase()}-card`}
            className={tier.cardClass}
          >
            {tier.urgency && (
              <span
                data-testid="tier-diamond-urgency"
                className="gold-glow absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-fira-gold px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-fira-deep"
              >
                <Flame className="h-3 w-3" />
                {tier.urgency}
              </span>
            )}
            <div className="flex flex-col items-center text-center">
              <tier.Icon className={`h-10 w-10 ${tier.iconClass}`} />
              <h3 className="font-display mt-4 text-2xl font-semibold uppercase tracking-widest">
                {tier.name}
              </h3>
              <p className={`font-display mt-2 text-5xl font-bold ${tier.priceClass}`}>
                {tier.price}
              </p>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/50">
                Trading Deposit
              </span>
            </div>
            <ul className="mt-8 space-y-3">
              {tier.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm text-white/80">
                  <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tier.checkClass}`} />
                  <span>{perk}</span>
                </li>
              ))}
              {tier.crossed?.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm text-white/30">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-white/25" />
                  <span className="line-through">{perk}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
      <div
        data-testid="experience-note"
        className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-fira-pink/30 bg-fira-pink/10 px-6 py-4"
      >
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-fira-rose" />
        <p className="text-sm leading-relaxed text-white/80">
          <span className="font-bold text-white">NO EVENT TICKET FEE.</span> Deposit dibuat ke dalam akaun trading broker anda sendiri, bukan bayaran tiket atau bayaran kepada penganjur. Kategori seating ditentukan berdasarkan deposit trading yang layak dan disahkan oleh admin.
        </p>
      </div>
      <div className="mt-10 flex flex-col items-center gap-4">
        <Button
          onClick={onReserve}
          data-testid="experience-cta-btn"
          className="btn-lux rounded-full px-10 py-6 text-sm font-bold tracking-widest text-white transition-all duration-300 hover:scale-105"
        >
          CONTACT ADMIN TO RESERVE SEAT
        </Button>
        <p className="max-w-xl text-center text-xs leading-relaxed text-white/50">
          Semua kategori tertakluk kepada ketersediaan tempat dan syarat pengesahan deposit.
        </p>
      </div>
    </div>
  </section>
);
